import { collectionGroup, getDocs, query, where, writeBatch, Timestamp } from 'firebase/firestore';
import { db } from '../../../config';
import { DictionaryColumnMapping } from '../../../../../types/dictionary';
import { type ItemListType } from '../../../../../types/common';

/**
 * item_list から item_id → 表示ラベルのマップを作る。
 * ラベル規則は DictionaryMappingFormModal の patternItems と揃える:
 *   - 子あり: 子ごとに `${親} > ${子}`
 *   - 子なし: 親 `${親}`
 */
const buildItemLabelMap = (itemList: ItemListType[]): Map<string, string> => {
  const map = new Map<string, string>();
  for (const item of itemList) {
    const children = item.children ?? [];
    if (children.length > 0) {
      for (const child of children) {
        map.set(child.id, `${item.parentValue} > ${child.childValue}`);
      }
    } else {
      map.set(item.id, item.parentValue);
    }
  }
  return map;
};

/**
 * パターンの項目名（item_list）やパターン名が変更された際に、そのパターンを参照している
 * 全辞書マッピングの保持ラベル（search_item_label / column_mappings[].item_label /
 * pattern_name）を最新値に更新する。
 *
 * 変換・反映ロジックは item_id / pattern_id を基準に動くため、これらは表示用。
 * ただし古い名前が UI に残ると混乱するため、保存値も最新へ同期する。
 * 辞書は Firestore のみ（Cloud SQL を使わない）ので Firestore の更新だけで完結する。
 *
 * @param patternId   更新されたパターンID
 * @param itemList    更新後の item_list（camelCase）
 * @param patternName 更新後のパターン名（省略時は pattern_name を更新しない）
 */
export const syncDictionaryMappingLabels = async (
  patternId: string,
  itemList: ItemListType[],
  patternName?: string,
): Promise<void> => {
  if (!patternId) return;

  const labelMap = buildItemLabelMap(itemList);

  const mappingsSnap = await getDocs(
    query(collectionGroup(db, 'dictionary_mappings'), where('pattern_id', '==', patternId)),
  );
  if (mappingsSnap.empty) return;

  const batch = writeBatch(db);
  let hasUpdate = false;

  for (const mappingDoc of mappingsSnap.docs) {
    const data = mappingDoc.data();
    const updates: Record<string, string | DictionaryColumnMapping[] | Timestamp> = {};

    // 照合キー（search_item_id）のラベル
    const searchItemId = data.search_item_id as string | undefined;
    if (searchItemId) {
      const latest = labelMap.get(searchItemId);
      if (latest !== undefined && latest !== data.search_item_label) {
        updates.search_item_label = latest;
      }
    }

    // 取得項目（column_mappings）の格納先ラベル
    const columnMappings = (data.column_mappings as DictionaryColumnMapping[] | undefined) ?? [];
    let columnsChanged = false;
    const nextColumns = columnMappings.map(cm => {
      const latest = cm.item_id ? labelMap.get(cm.item_id) : undefined;
      if (latest !== undefined && latest !== cm.item_label) {
        columnsChanged = true;
        return { ...cm, item_label: latest };
      }
      return cm;
    });
    if (columnsChanged) {
      updates.column_mappings = nextColumns;
    }

    // パターン名
    if (patternName !== undefined && patternName !== data.pattern_name) {
      updates.pattern_name = patternName;
    }

    if (Object.keys(updates).length > 0) {
      updates.updated_at = Timestamp.now();
      batch.update(mappingDoc.ref, updates);
      hasUpdate = true;
    }
  }

  if (hasUpdate) {
    await batch.commit();
  }
};
